import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];
  const activeSlug = activeCategory.slug;

  const filteredComics = useMemo(() => {
    if (activeSlug === 'recommend') {
      return [...comics].sort((a, b) => b.rating - a.rating);
    }
    if (activeSlug === 'completed') {
      return comics.filter((c) => c.status === '已完结');
    }
    return comics.filter((c) => c.category === activeSlug);
  }, [activeSlug]);

  const totalPages = Math.max(1, Math.ceil(filteredComics.length / ITEMS_PER_PAGE));
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  return (
    <div className="pt-16">
      <PageSEO
        title={`${activeCategory.name}韩漫`}
        description={`韩漫屋${activeCategory.name}分类 - ${activeCategory.description}。浏览海量${activeCategory.name}类韩国漫画，免费高清在线阅读，每日更新热门韩漫。`}
        keywords={`${activeCategory.name}韩漫,韩国漫画,${activeCategory.name}漫画,韩漫屋`}
        canonicalUrl={`https://hanmanwu.com/category/${activeSlug}`}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '漫画分类', path: '/category/recommend' },
          { label: activeCategory.name },
        ]}
      />

      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
          {activeCategory.name}韩漫
        </h1>
        <p className="text-sm text-foreground-500 mb-6">{activeCategory.description}</p>

        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 flex-nowrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => handleCategoryChange(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {filteredComics.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto mb-3"></i>
            <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}